import { Pill } from '~/components';
import type { PillTypes } from '~/components/Pill/Pill';
import { CALENDAR_OUTLINE_BUTTON } from '~/features/shared/calendar/controlStyles';
import { formatDisplayDate } from '~/features/shared/calendar/dateUtils';
import { contractStatusLabel } from '../contractMarkers';
import type { CalendarContract } from '../types';

const STATUS_PILL_TYPE: Record<string, PillTypes> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  COMPLETED: 'success',
  TERMINATED: 'danger',
};

const contractSpan = (contract: CalendarContract): string =>
  contract.endDate
    ? `${formatDisplayDate(contract.startDate)} to ${formatDisplayDate(contract.endDate)}`
    : `Starts ${formatDisplayDate(contract.startDate)}`;

interface CalendarContractListProps {
  contracts: readonly CalendarContract[];
  /** Cy tag prefix, so the page list and the editor list stay distinguishable. */
  cyPrefix: string;
  onViewContract: (contractId: number) => void;
}

/**
 * [MCV-1844] Contracts covering the selected date(s), each in the status it is in, with a
 * `View contract` action. Read-only: the calendar never writes contracts.
 */
export const CalendarContractList = ({
  contracts,
  cyPrefix,
  onViewContract,
}: CalendarContractListProps) => (
  <ul className="space-y-2" data-cy={`${cyPrefix}-list`}>
    {contracts.map((contract) => (
      <li
        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-grey-900 bg-grey-950 p-3"
        data-cy={`${cyPrefix}-${contract.contractId}`}
        key={contract.contractId}
      >
        <div className="min-w-0 text-left">
          <p className="truncate body-text-sb text-grey-100">{contract.title}</p>
          <p className="mt-0.5 text-xs text-grey-500">{contractSpan(contract)}</p>
        </div>
        <div className="flex items-center gap-3">
          <Pill
            cyTag={`${cyPrefix}-status-${contract.contractId}`}
            label={contractStatusLabel(contract.status)}
            type={STATUS_PILL_TYPE[contract.status] ?? 'neutral'}
          />
          <button
            aria-label={`View contract: ${contract.title}`}
            className={CALENDAR_OUTLINE_BUTTON}
            data-cy={`${cyPrefix}-view-${contract.contractId}`}
            onClick={() => onViewContract(contract.contractId)}
            type="button"
          >
            View contract
          </button>
        </div>
      </li>
    ))}
  </ul>
);
